import React, { useEffect, useState } from "react";
import "../ContentPages/style.css";
import "../ContentPages/table.css";
import BackButton from "../Buttons/BackButton";
import ArrowDropIcon from "../Icons/ArrowDropIcon.jsx";
import RightCircleIcon from "../Icons/RightCircleIcon.jsx";
import LeftCircleIcon from "../Icons/LeftCircleIcon.jsx";
import { getAssertionHistory } from "../../Services/applicationService";
import Loader from "../Loader/Loader";

const MySharedInformation = ({ handleBack }) => {
  const [sharedData, setSharedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("desc"); // State for sort order: 'asc' or 'desc'
  const itemsPerPage = 15;

  useEffect(() => {
    const fetchSharedInfo = async () => {
      try {
        const data = await getAssertionHistory();
        const assertions = data?.assertionHistory || [];
        
        // Sort by date descending by default
        const sortedData = [...assertions].sort((a, b) => {
          const dateStrA = a.IsMigrated ? a.ConsentEventDate : a.CreatedDateTime;
          const dateStrB = b.IsMigrated ? b.ConsentEventDate : b.CreatedDateTime;
          const dateA = parseDate(dateStrA);
          const dateB = parseDate(dateStrB);
          if (!dateA || !dateB) return 0;
          return dateB - dateA;
        });
        
        setSharedData(sortedData);
      } catch (error) {
        console.error("Error fetching shared information:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSharedInfo();
  }, []);

  const parseDate = (input) => {
    if (!input) return null;
    
    // Try dd/MM/yyyy HH:mm:ss format FIRST
    const parts = input.trim().split(/[\s\/:]/).filter(Boolean);
    if (parts.length >= 6) {
      const [dd, mm, yyyy, hh, min, sec] = parts.map(Number);
      const date = new Date(yyyy, mm - 1, dd, hh, min, sec);
      if (!isNaN(date)) return date;
    }
    
    // Fall back to ISO format
    const iso = new Date(input);
    if (!isNaN(iso)) return iso;
    
    return null;
  };

  const formatDate = (input) => {
    // Same formatting as admin:AssertionHistory
    // input: "dd/MM/yyyy HH:mm:ss" in UTC
    if (!input) return "";

    const m = String(input)
      .trim()
      .match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return input; // fallback
    const [, day, month, year, hh, mm, ss] = m;

    // Treat input as UTC instant
    const utcDate = new Date(
      Date.UTC(+year, +month - 1, +day, +(hh || 0), +(mm || 0), +(ss || 0))
    );

    // Format in target zone
    return new Intl.DateTimeFormat("en-NZ", {
      timeZone: "Pacific/Auckland",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .format(utcDate)
      .replace(",", ""); // remove comma between date and time
  };

  const correctSPName = (name) => {
    if (name.includes("DELETED")) {
      return name.replace("_DELETED", "");
    }
    return name;
  };

  // Function to handle sorting
  const handleSort = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);

    const sortedData = [...sharedData].sort((a, b) => {
      const dateStrA = a.IsMigrated ? a.ConsentEventDate : a.CreatedDateTime;
      const dateStrB = b.IsMigrated ? b.ConsentEventDate : b.CreatedDateTime;
      const dateA = parseDate(dateStrA);
      const dateB = parseDate(dateStrB);

      if (!dateA || !dateB) return 0; // Handle invalid dates
      return newSortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    setSharedData(sortedData);
  };

  const totalItems = sharedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = sharedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="shared-info-container">
      {loading && <Loader />}

      <h1 className="intro-title-mi" lang="mi-NZ">
        Aku mōhiohio tiri
      </h1>
      <hr className="shared-info-hr" />
      <h1 className="intro-title">My shared information</h1>
      <p className="table-description">
        This table shows when you've used your RealMe verified identity to
        access an online service.
      </p>

      <table className="shared-info-table">
        <thead>
          <tr>
            <th>
              <div
                className="date-time-header"
                onClick={handleSort}
                style={{ cursor: "pointer" }}
              >
                Date / Time
                <ArrowDropIcon
                  style={{
                    transform:
                      sortOrder === "asc" ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </div>
            </th>
            <th>Service agency</th>
            <th>Information shared</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length > 0 ? (
            currentItems.map((item, index) => (
              <tr key={index}>
                <td>
                  {formatDate(
                    item.IsMigrated
                      ? item.ConsentEventDate
                      : item.CreatedDateTime
                  )}
                </td>
                <td>{correctSPName(item.ServiceProviderName)}</td>
                <td>{item.ConsentAttributesRequested}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3">No data available</td>
            </tr>
          )}
        </tbody>
      </table>

      {currentItems.length > 0 && totalPages > 1 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{" "}
            <strong>{Math.min(currentPage * itemsPerPage, totalItems)}</strong>{" "}
            of <strong>{totalItems}</strong>
          </div>
          <div className="pagination-controls">
            {currentPage > 1 && <LeftCircleIcon onClick={handlePreviousPage} />}
            {(() => {
              const maxVisiblePages = 4;
              const halfVisible = Math.floor(maxVisiblePages / 2);
              let startPage = Math.max(1, currentPage - halfVisible);
              let endPage = Math.min(
                totalPages,
                startPage + maxVisiblePages - 1
              );

              if (endPage - startPage < maxVisiblePages - 1) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              const pages = [];

              if (startPage > 1) {
                pages.push(
                  <button
                    key={1}
                    onClick={() => setCurrentPage(1)}
                    className="page-btn"
                  >
                    1
                  </button>
                );
                if (startPage > 2) {
                  pages.push(
                    <span key="ellipsis-start" className="pagination-ellipsis">
                      ...
                    </span>
                  );
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`page-btn ${currentPage === i ? "active" : ""}`}
                  >
                    {i}
                  </button>
                );
              }

              // Show ellipsis and last page if needed
              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(
                    <span key="ellipsis-end" className="pagination-ellipsis">
                      ...
                    </span>
                  );
                }
                pages.push(
                  <button
                    key={totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="page-btn"
                  >
                    {totalPages}
                  </button>
                );
              }

              return pages;
            })()}
            {currentPage < totalPages && (
              <RightCircleIcon onClick={handleNextPage} />
            )}
          </div>
        </div>
      )}

      <div className="step-container-nospace back-button-container">
        <BackButton text="Back" onClick={handleBack} />
      </div>
    </div>
  );
};

export default MySharedInformation;